import { create } from 'zustand';
import { TYPE_CONFIG } from './constants';
import {
  applyNodeChanges,
  applyEdgeChanges,
  addEdge,
  MarkerType
} from '@xyflow/react';

const makeId = () => Math.random().toString(36).substr(2, 9);

const getNextCustomId = (nodes, offset = 0) => {
  const ids = nodes.map(n => parseInt(n.data?.customId || '0')).filter(n => !isNaN(n));
  const max = ids.length > 0 ? Math.max(...ids) : nodes.length;
  return (max + 1 + offset).toString();
};

const MAX_HISTORY = 100;

export const useStore = create((set, get) => {

  // ── Internal helper: snapshot current nodes+edges into past ──────────────
  const pushHistory = () => {
    const { nodes, edges, _history } = get();
    const snap = { nodes: JSON.parse(JSON.stringify(nodes)), edges: JSON.parse(JSON.stringify(edges)) };
    set({
      _history: [..._history, snap].slice(-MAX_HISTORY),
      _future: [],
    });
  };

  // ── Edge factory (no animation) ──────────────────────────────────────────
  const makeEdge = (id, source, target, sourceHandle, extra = {}) => ({
    id,
    source,
    target,
    sourceHandle: sourceHandle ?? null,
    type: 'custom',
    animated: false,
    markerEnd: { type: MarkerType.ArrowClosed },
    ...extra,
  });

  return {
    nodes: [],
    edges: [],
    selectedNodeId: null,

    // ── Undo / Redo history ────────────────────────────────────────────────
    _history: [],  // array of { nodes, edges } snapshots (past)
    _future: [],   // array of { nodes, edges } snapshots (future)

    undo: () => {
      const { _history, _future, nodes, edges } = get();
      if (_history.length === 0) return;
      const snap = _history[_history.length - 1];
      const current = { nodes: JSON.parse(JSON.stringify(nodes)), edges: JSON.parse(JSON.stringify(edges)) };
      set({
        nodes: snap.nodes,
        edges: snap.edges,
        _history: _history.slice(0, -1),
        _future: [current, ..._future].slice(0, MAX_HISTORY),
      });
    },

    redo: () => {
      const { _history, _future, nodes, edges } = get();
      if (_future.length === 0) return;
      const snap = _future[0];
      const current = { nodes: JSON.parse(JSON.stringify(nodes)), edges: JSON.parse(JSON.stringify(edges)) };
      set({
        nodes: snap.nodes,
        edges: snap.edges,
        _history: [..._history, current].slice(-MAX_HISTORY),
        _future: _future.slice(1),
      });
    },

    canUndo: () => get()._history.length > 0,
    canRedo: () => get()._future.length > 0,

    takeSnapshot: () => {
      pushHistory();
    },

    // ── ReactFlow change handlers ──────────────────────────────────────────
    // Node drags come through here as 'position' changes. We snapshot on drag-start
    // instead of drag-end to properly capture the pre-drag positions.
    onNodesChange: (changes) => {
      const shouldSnapshot = changes.some(
        c => c.type === 'remove'
      );
      if (shouldSnapshot) pushHistory();
      set({ nodes: applyNodeChanges(changes, get().nodes) });
    },

    onEdgesChange: (changes) => {
      const shouldSnapshot = changes.some(c => c.type === 'remove');
      if (shouldSnapshot) pushHistory();
      set({ edges: applyEdgeChanges(changes, get().edges) });
    },

    onConnect: (connection) => {
      pushHistory();
      const edge = makeEdge(
        `e-${connection.source}-${connection.target}-${makeId()}`,
        connection.source,
        connection.target,
        connection.sourceHandle,
      );
      set({ edges: addEdge(edge, get().edges) });
    },

    // ── Low-level setters (used by undo/redo in FlowBuilder) ───────────────
    setNodes: (nodes) => set({ nodes }),
    setEdges: (edges) => set({ edges }),
    addNode: (node) => {
      pushHistory();
      set({ nodes: [...get().nodes, node] });
    },

    // ── Add node by type (sidebar click) ──────────────────────────────────
    addNodeByType: (type) => {
      if (type === 'action') {
        get().createWebhookGroup();
        return;
      }
      pushHistory();
      const id = makeId();
      const currentNodes = get().nodes;
      const position = currentNodes.length > 0
        ? { x: currentNodes[currentNodes.length - 1].position.x + 80, y: currentNodes[currentNodes.length - 1].position.y + 80 }
        : { x: 150, y: 150 };

      const newNode = {
        id,
        type: 'custom',
        position,
        data: {
          label: TYPE_CONFIG[type]?.label || `${type.charAt(0).toUpperCase() + type.slice(1)}`,
          type,
          customId: getNextCustomId(currentNodes),
          messages: type === 'message' ? [{ id: '1', type: 'text', content: 'Hello!' }] : undefined,
          aiConfig: type === 'ai_agent' ? {
            systemPromptOverride: '',
            userInput: '{{lastUserInput}}',
            outputVariable: 'aiResponse',
          } : undefined,
          functionConfig: type === 'function' ? {
            code: '// Your code here\n// Return { key: value } to set variables\nreturn {};',
            inputMapping: {},
            outputMapping: {}
          } : undefined,
          integrationConfig: type === 'integration' ? {
            integrationType: '',
            action: '',
          } : undefined,
        },
      };
      set({ nodes: [...currentNodes, newNode] });
    },

    // ── Add node at drag-drop position ────────────────────────────────────
    addNodeAtPosition: (type, position) => {
      if (type === 'action') {
        const currentNodes = get().nodes;
        pushHistory();
        const webhookId = makeId();
        const successId = makeId();
        const failureId = makeId();

        const webhookNode = {
          id: webhookId, type: 'custom', position,
          data: {
            label: 'Webhook', type: 'action',
            customId: getNextCustomId(currentNodes, 0),
            webhook: { name: '', url: '', method: 'POST', verificationToken: '', basicAuthUsername: '', basicAuthPassword: '', headers: [], params: [], body: '', timeout: 10000, retries: 0, mappings: [], connectionStatus: 'idle' },
          },
        };
        const successNode = {
          id: successId, type: 'custom',
          position: { x: position.x + 280, y: position.y - 70 },
          data: { label: 'Success', type: 'webhook_result', webhookBranch: 'success', parentWebhookId: webhookId, customId: getNextCustomId(currentNodes, 1) },
        };
        const failureNode = {
          id: failureId, type: 'custom',
          position: { x: position.x + 280, y: position.y + 70 },
          data: { label: 'Failure', type: 'webhook_result', webhookBranch: 'failure', parentWebhookId: webhookId, customId: getNextCustomId(currentNodes, 2) },
        };

        const groupEdges = [
          makeEdge(`e-${webhookId}-${successId}`, webhookId, successId, 'success'),
          makeEdge(`e-${webhookId}-${failureId}`, webhookId, failureId, 'failure'),
        ];

        set({ nodes: [...currentNodes, webhookNode, successNode, failureNode], edges: [...get().edges, ...groupEdges] });
        return;
      }

      pushHistory();
      const id = makeId();
      const newNode = {
        id, type: 'custom', position,
        data: {
          label: `${type.charAt(0).toUpperCase() + type.slice(1)}`,
          type,
          customId: getNextCustomId(get().nodes),
          messages: type === 'message' ? [{ id: '1', type: 'text', content: 'Hello!' }] : undefined,
          aiConfig: type === 'ai_agent' ? { systemPromptOverride: '', userInput: '{{lastUserInput}}', outputVariable: 'aiResponse' } : undefined,
          functionConfig: type === 'function' ? { code: '// Your code here\n// Return { key: value } to set variables\nreturn {};', inputMapping: {}, outputMapping: {} } : undefined,
          integrationConfig: type === 'integration' ? { integrationType: '', action: '' } : undefined,
        },
      };
      set({ nodes: [...get().nodes, newNode] });
    },

    // ── Webhook group helper ───────────────────────────────────────────────
    createWebhookGroup: (sourceId, sourceHandle) => {
      pushHistory();
      const currentNodes = get().nodes;
      const webhookId = makeId();
      const successId = makeId();
      const failureId = makeId();

      const parentNode = sourceId ? currentNodes.find(n => n.id === sourceId) : null;
      const basePos = parentNode ? { x: parentNode.position.x + 280, y: parentNode.position.y } : { x: 250, y: 150 };

      const webhookNode = {
        id: webhookId, type: 'custom', position: basePos,
        data: { label: 'Webhook', type: 'action', customId: getNextCustomId(currentNodes, 0), webhook: { name: '', url: '', method: 'POST', verificationToken: '', basicAuthUsername: '', basicAuthPassword: '', headers: [], params: [], body: '', timeout: 10000, retries: 0, mappings: [], connectionStatus: 'idle' } },
      };
      const successNode = {
        id: successId, type: 'custom', position: { x: basePos.x + 280, y: basePos.y - 70 },
        data: { label: 'Success', type: 'webhook_result', webhookBranch: 'success', parentWebhookId: webhookId, customId: getNextCustomId(currentNodes, 1) },
      };
      const failureNode = {
        id: failureId, type: 'custom', position: { x: basePos.x + 280, y: basePos.y + 70 },
        data: { label: 'Failure', type: 'webhook_result', webhookBranch: 'failure', parentWebhookId: webhookId, customId: getNextCustomId(currentNodes, 2) },
      };

      const parentEdge = sourceId ? [makeEdge(`e-${sourceId}-${webhookId}`, sourceId, webhookId, sourceHandle)] : [];
      const groupEdges = [
        makeEdge(`e-${webhookId}-${successId}`, webhookId, successId, 'success'),
        makeEdge(`e-${webhookId}-${failureId}`, webhookId, failureId, 'failure'),
      ];

      set({ nodes: [...currentNodes, webhookNode, successNode, failureNode], edges: [...get().edges, ...parentEdge, ...groupEdges] });
    },

    // ── Update node data (property panel edits) ───────────────────────────
    updateNodeData: (nodeId, data) => {
      pushHistory();
      set({
        nodes: get().nodes.map(node =>
          node.id === nodeId ? { ...node, data: { ...node.data, ...data } } : node
        ),
      });
    },

    // ── Delete node ───────────────────────────────────────────────────────
    deleteNode: (nodeId) => {
      pushHistory();
      const node = get().nodes.find(n => n.id === nodeId);
      const childIds = node?.data?.type === 'action'
        ? get().nodes.filter(n => n.data?.parentWebhookId === nodeId).map(n => n.id)
        : [];
      const idsToRemove = new Set([nodeId, ...childIds]);

      set({
        nodes: get().nodes.filter(n => !idsToRemove.has(n.id)),
        edges: get().edges.filter(e => !idsToRemove.has(e.source) && !idsToRemove.has(e.target)),
        selectedNodeId: idsToRemove.has(get().selectedNodeId || '') ? null : get().selectedNodeId,
      });
    },

    // ── Duplicate node ─────────────────────────────────────────────────────
    duplicateNode: (nodeId) => {
      const node = get().nodes.find(n => n.id === nodeId);
      if (!node) return;
      if (node.data?.type === 'webhook_result') return;
      if (node.data?.type === 'action') { get().createWebhookGroup(); return; }

      pushHistory();
      const newNode = {
        ...node,
        id: makeId(),
        position: { x: node.position.x + 40, y: node.position.y + 40 },
        data: { ...node.data, customId: getNextCustomId(get().nodes) },
        selected: false,
      };
      set({ nodes: [...get().nodes, newNode] });
    },

    setSelectedNodeId: (id) => set({ selectedNodeId: id }),

    // ── Insert node on edge ────────────────────────────────────────────────
    insertNodeOnEdge: (edgeId, type) => {
      const edge = get().edges.find(e => e.id === edgeId);
      if (!edge) return;
      const sourceNode = get().nodes.find(n => n.id === edge.source);
      const targetNode = get().nodes.find(n => n.id === edge.target);
      if (!sourceNode || !targetNode) return;

      if (type === 'action') {
        pushHistory();
        set({ edges: get().edges.filter(e => e.id !== edgeId) });
        get().createWebhookGroup(edge.source, edge.sourceHandle || undefined);
        return;
      }

      pushHistory();
      const newNodeId = makeId();
      const newNode = {
        id: newNodeId, type: 'custom',
        position: {
          x: (sourceNode.position.x + targetNode.position.x) / 2,
          y: (sourceNode.position.y + targetNode.position.y) / 2,
        },
        data: {
          label: `${type.charAt(0).toUpperCase() + type.slice(1)}`,
          type, customId: getNextCustomId(get().nodes),
          messages: type === 'message' ? [{ id: '1', type: 'text', content: 'Hello!' }] : undefined,
        },
      };

      const newEdges = [
        makeEdge(`e-${edge.source}-${newNodeId}`, edge.source, newNodeId, edge.sourceHandle),
        makeEdge(`e-${newNodeId}-${edge.target}`, newNodeId, edge.target, null),
      ];

      set({ nodes: [...get().nodes, newNode], edges: [...get().edges.filter(e => e.id !== edgeId), ...newEdges] });
    },

    // ── Attach / detach subtree ────────────────────────────────────────────
    detachSubtree: (nodeId) => {
      pushHistory();
      set({ edges: get().edges.filter(e => e.target !== nodeId) });
    },

    attachSubtree: (nodeId, targetId) => {
      pushHistory();
      const edgesWithout = get().edges.filter(e => e.target !== nodeId);
      set({ edges: [...edgesWithout, makeEdge(`e-${targetId}-${nodeId}`, targetId, nodeId, null)] });
    },
    
    // ── Add child node to existing node (node "+" menu) ───────────────────
addChildNode: (sourceId, type, handleId) => {
  if (type === 'action') {
    get().createWebhookGroup(sourceId, handleId);
    return;
  }

  const id = makeId();
  const parentNode = get().nodes.find(n => n.id === sourceId);
  const newNode = {
    id,
    type: 'custom',
    position: parentNode
      ? { x: parentNode.position.x + 280, y: parentNode.position.y + (handleId === 'yes' ? -80 : handleId === 'no' ? 80 : 0) }
      : { x: 250, y: 150 },
    data: {
      label: TYPE_CONFIG[type]?.label || `${type.charAt(0).toUpperCase() + type.slice(1)}`,
      type,
      customId: getNextCustomId(get().nodes),
      messages: type === 'message' ? [{ id: '1', type: 'text', content: 'Hello!' }] : undefined,
      aiConfig: type === 'ai_agent' ? {
        systemPromptOverride: '',
        userInput: '{{lastUserInput}}',
        outputVariable: 'aiResponse',
      } : undefined,
      functionConfig: type === 'function' ? {
        code: '// Your code here\n// Return { key: value } to set variables\nreturn {};',
        inputMapping: {},
        outputMapping: {}
      } : undefined,
    },
  };

  const newEdge = {
    id: `e-${sourceId}-${id}`,
    source: sourceId,
    target: id,
    sourceHandle: handleId,
    animated: true,
    type: 'custom',
    markerEnd: { type: MarkerType.ArrowClosed },
  };

  pushHistory();
  set({ nodes: [...get().nodes, newNode], edges: addEdge(newEdge, get().edges) });
},

    // ── Load flow (no history entry — replaces everything) ────────────────
    loadFlowData: (flowData) => {
      if (flowData && flowData.nodes) {
        const hydrateNodeData = (node) => {
          const { type } = node.data;
          const base = {
            ...node.data,
            label: node.data.label || type,
            type,
            customId: node.data.customId || '1',
          };

          if (type === 'message') {
            return { ...base, messages: node.data.messages?.length ? node.data.messages : [{ id: '1', type: 'text', content: '' }] };
          }
          if (type === 'action') {
            return {
              ...base,
              webhook: node.data.webhook || { name: '', url: '', method: 'POST', verificationToken: '', basicAuthUsername: '', basicAuthPassword: '', headers: [], params: [], body: '', timeout: 10000, retries: 0, mappings: [], connectionStatus: 'idle' },
            };
          }
          if (type === 'webhook_result') {
            return { ...base, webhookBranch: node.data.webhookBranch || 'success', parentWebhookId: node.data.parentWebhookId || '' };
          }
          if (type === 'condition') {
            return {
              ...base,
              branches: (node.data.branches || []).map(b => ({
                id: b.id,
                label: b.label || b.id,
                targetNodeId: b.targetNodeId || null,
                rule: b.rule || { field: 'lastUserInput', operator: 'equals', value: '' },
              })),
              defaultTargetNodeId: node.data.defaultTargetNodeId || null,
            };
          }
          if (type === 'ai_agent') {
            return { ...base, aiConfig: node.data.aiConfig || { systemPromptOverride: '', userInput: '{{lastUserInput}}', outputVariable: 'aiResponse' } };
          }
          if (type === 'function') {
            return { ...base, functionConfig: node.data.functionConfig || { code: '// Your code here\n// Return { key: value } to set variables\nreturn {};', inputMapping: {}, outputMapping: {} } };
          }
          if (type === 'integration') {
            return { ...base, integrationConfig: node.data.integrationConfig || { integrationType: '', action: '' } };
          }
          if (type === 'input') {
            return { ...base, inputConfig: node.data.inputConfig || { variable: 'userInput', validationType: 'none', errorMessage: '' } };
          }
          if (type === 'delay') {
            return { ...base, delayMs: node.data.delayMs ?? 1000 };
          }
          // start, escalate, goto, attribute — no extra defaults needed
          return base;
        };

        const hydratedNodes = flowData.nodes.map(node => ({
          ...node,
          data: hydrateNodeData(node),
        }));

        const normalizedEdges = (flowData.edges || []).map(e => ({
          ...e,
          markerEnd: e.markerEnd
            ? { ...e.markerEnd, type: e.markerEnd.type?.toLowerCase() }
            : { type: MarkerType.ArrowClosed },
        }));

        set({ nodes: hydratedNodes, edges: normalizedEdges, selectedNodeId: null, _history: [], _future: [] });
      } else {
        const initialNodes = [{
          id: 'start', type: 'custom', position: { x: 50, y: 50 },
          data: { label: 'Starting Point', type: 'start', customId: '1' },
        }];
        set({ nodes: initialNodes, edges: [], selectedNodeId: null, _history: [], _future: [] });
      }
    },

    // ── Legacy flow persistence (kept for compatibility) ──────────────────
    currentFlowId: null,
    currentFlowName: 'Untitled Flow',
    setCurrentFlow: (id, name) => set({ currentFlowId: id, currentFlowName: name }),

    saveFlow: async (flowId, name) => {
      const { nodes, edges, currentFlowId, currentFlowName } = get();
      const idToSave = flowId || currentFlowId || `flow-${Date.now()}`;
      const nameToSave = name || currentFlowName;
      try {
        const response = await fetch('/api/flows', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ flowId: idToSave, name: nameToSave, flowData: { nodes, edges } })
        });
        const result = await response.json();
        if (result.success) {
          set({ currentFlowId: idToSave, currentFlowName: nameToSave });
          return { success: true };
        }
        return { success: false, error: result.error || 'Unknown error' };
      } catch (e) {
        console.error('Failed to save flow', e);
        return { success: false, error: 'Network error or backend server offline.' };
      }
    },

    loadFlow: async (flowId) => {
      try {
        const response = await fetch(`/api/flows/${flowId}`);
        const result = await response.json();
        if (result.success && result.data) {
          const { nodes, edges } = result.data.flowData;
          set({ nodes: nodes || [], edges: edges || [], currentFlowId: result.data.flowId, currentFlowName: result.data.name || 'Untitled Flow', selectedNodeId: null, _history: [], _future: [] });
        }
      } catch (e) {
        console.error('Failed to load flow', e);
      }
    },

    deleteFlow: async (flowId) => {
      try {
        const response = await fetch(`/api/flows/${flowId}`, { method: 'DELETE' });
        const result = await response.json();
        if (!result.success) throw new Error(result.error || 'Unknown error');
        if (get().currentFlowId === flowId) {
          set({ currentFlowId: null, currentFlowName: 'Untitled Flow', nodes: [], edges: [] });
        }
      } catch (e) {
        console.error('Failed to delete flow', e);
        throw e;
      }
    },

    listFlows: async () => {
      try {
        const response = await fetch('/api/flows');
        const result = await response.json();
        return result.success ? result.data : [];
      } catch (e) {
        console.error('Failed to list flows', e);
        return [];
      }
    },
  };
});
